import * as Sentry from '@sentry/react-router';
/**
 * By default, Remix will handle hydrating your app on the client for you.
 * You are free to delete this file if you'd like to, but if you ever want it revealed again, you can run `npx remix reveal` ✨
 * For more information, see https://remix.run/file-conventions/entry.client
 */

import { PostHogProvider } from '@posthog/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { startTransition, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import { HydratedRouter } from 'react-router/dom';
import { cookieAuth, getRouterContext } from './client/browser.client';
import { env } from './env/client';

if (env.VITE_APP_ENV === 'production') {
  posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_API_HOST,
    defaults: '2026-01-30',
    enable_heatmaps: true,
    person_profiles: 'identified_only',
    disable_session_recording: false,
    addTracingHeaders: [
      window.location.hostname,
      env.VITE_IDENTITYKIT_PAYLEO_BASE_URL,
      env.VITE_EWA_PAYLEO_BASE_URL,
      'localhost',
    ],
  });

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterTracingIntegration({
        useEffect: useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 0.8,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
});

// Pull the auth state from the cookies before the router starts,
// so that the first `getRouterContext()` call sees the session.
cookieAuth.syncAuthState();

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <PostHogProvider client={posthog}>
        <QueryClientProvider client={queryClient}>
          <HydratedRouter getContext={getRouterContext} />
        </QueryClientProvider>
      </PostHogProvider>
    </StrictMode>
  );
});

// Setting up persistance of auth state to browser
// const auth = getIdentityAuth();
// attachLocalStoragePersistance(auth);
// auth.onAuthStateChange((state) => {
//   if (state === null) {
//     // when logging out, invalidate all queries
//     queryClient.invalidateQueries();
//   }
// });
